'use client';

import React, { useEffect, useState, useMemo, memo, useCallback } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from '@/app/lib/axios';
import MatrixGrid from '@/app/components/MatrixGrid';
import { Clock, TrendingUp, Frown, Info } from 'lucide-react';
import { Leaderboard } from "@/types/leaderboard";
import { Participant } from "@/types/participant";
import Image from 'next/image';
import Link from 'next/link';
import { CoinIcon } from "@/app/components/SvgIcons";
import SportLeaderboardView from '@/app/components/SportLeaderboardView';

const leaderboardsConfig = [
    {
        id: 'stake',
        title: 'STAKE.COM',
        logo: '/img/logos/stakecom.svg',
        refferal_url: 'https://stake.com/?offer=sokoloco&c=c9bb838557',
        mobile_logo: '/img/logos/stakecom.svg',
        icon: null,
    },
    // {
    //     id: 'sport',
    //     title: 'SPORTS BETTING',
    //     logo: '/img/logos/fifaworldcuplogo.webp',
    //     refferal_url: 'https://stake.com/?offer=sokoloco&c=c9bb838557',
    //     mobile_logo: '/img/logos/fifaworldcuplogo.webp',
    //     icon: null,
    // },
];

// Bonus points per placement - EDIT THIS SECTION
const bonusPointsPlacements: Record<number, number> = {
    1: 3000,
    2: 2000,
    3: 1500,
    4: 1000,
    5: 800,
    6: 550,
    7: 450,
    8: 325,
    9: 225,
    10: 150,
};

const totalBonusPoints = Object.values(bonusPointsPlacements).reduce((a, b) => a + b, 0);


const TimeDisplay = memo(({ value, label }: { value: number; label: string }) => (
    <div className="text-center">
        <div className="backdrop-blur border-b border-green-400/30 rounded-xl p-4 mb-2">
            <span className="text-xl md:text-3xl font-mono font-bold text-green-300">
                {String(value).padStart(2, '0')}
            </span>
            <span className="text-xs font-mono text-green-400"> {label}</span>
        </div>
    </div>
));
TimeDisplay.displayName = 'TimeDisplay';

const LeaderboardButton = memo(({
    lb,
    currentBoard,
    isMobile,
    onSwitch
}: {
    lb: typeof leaderboardsConfig[0];
    currentBoard: string;
    isMobile: boolean;
    onSwitch: (board: string) => void;
}) => (
    <button
        onClick={() => onSwitch(lb.id)}
        className="cursor-pointer bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 group relative overflow-hidden rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 hover: transition-all duration-200"
    >
        <div className="relative flex flex-col items-center p-4 md:p-6 text-center gap-3">
            <Image
                src={isMobile && lb.mobile_logo ? lb.mobile_logo : lb.logo}
                alt={lb.title}
                width={400}
                height={200}
                className="w-28 h-8 md:w-46 md:h-16 object-contain mb-2 drop-shadow-md"
                draggable="false"
                priority={currentBoard === lb.id}
            />
            <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent transform ${currentBoard === lb.id ? 'scale-x-100' : 'scale-x-0'} group-hover:scale-x-100 transition-transform`}></div>
        </div>
    </button>
));
LeaderboardButton.displayName = 'LeaderboardButton';

// Tooltip component for bonus points info
const BonusPointsTooltip = memo(() => {
    const [visible, setVisible] = useState(false);

    return (
        <div className="relative inline-flex items-center">
            <button
                onMouseEnter={() => setVisible(true)}
                onMouseLeave={() => setVisible(false)}
                onFocus={() => setVisible(true)}
                onBlur={() => setVisible(false)}
                className="text-green-400/60 hover:text-green-300 transition-colors duration-150 cursor-help"
                aria-label="Bonus Kick Points info"
            >
                <Info size={13} />
            </button>

            {visible && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 pointer-events-none">
                    <div className="backdrop-blur-sm bg-gray-900/95 rounded-xl p-3 shadow-xl shadow-black/60">
                        <p className="text-xs font-mono text-gray-200 text-center">You will receive extra Kick Points once the leaderboard ends.</p>
                    </div>
                </div>
            )}
        </div>
    );
});
BonusPointsTooltip.displayName = 'BonusPointsTooltip';

const TopPlayerCard = memo(({
    player,
    currentBoard,
    formatWagered,
    getPlacementPrize,
    getAvatarSrc,
    isCsgobigAvatar
}: {
    player: Participant;
    currentBoard: string;
    formatWagered: (amount: number) => { amount: string; icon: string | null };
    getPlacementPrize: (placement: number) => { amount: string; icon: string | null } | null;
    getAvatarSrc: (avatarPath: string | null | undefined) => string;
    isCsgobigAvatar: (avatarPath: string | null | undefined) => boolean;
}) => {
    const getPlacementColor = (placement: number) => {
        if (placement === 1) return 'from-yellow-400 via-yellow-500 to-amber-600';
        if (placement === 2) return 'from-gray-300 via-gray-400 to-gray-500';
        if (placement === 3) return 'from-orange-400 via-amber-600 to-orange-700';
        return 'from-green-400 via-emerald-500 to-green-600';
    };

    const bonusPoints = player.placement ? bonusPointsPlacements[player.placement] : null;

    return (
        <div
            className={`leaderboard-box m-4 md:m-8 backdrop-blur group relative overflow-hidden rounded-xl shadow-lg hover: transition-all duration-200 border-b-2 p-10 hover:scale-110 ${player.placement === 1 ? 'border-yellow-400 md:order-2 md:scale-110' :
                player.placement === 2 ? 'border-gray-400 md:order-1' :
                    'border-orange-400 md:order-3'
                }`}
        >
            <div className="relative z-10">
                <div className="flex justify-center mb-4">
                    <div className={`w-24 h-24 rounded-full border-4 overflow-hidden ${player.placement === 1 ? 'border-yellow-400' :
                        player.placement === 2 ? 'border-gray-400' :
                            'border-orange-400'
                        }`}>
                        <Image
                            src={getAvatarSrc(player.avatar)}
                            alt={player.username}
                            width={96}
                            height={96}
                            className="w-full h-full object-cover"
                            draggable="false"
                            referrerPolicy={isCsgobigAvatar(player.avatar) ? "no-referrer" : undefined}
                        />
                    </div>
                </div>

                <h3 className={`text-xl font-mono font-bold text-center mb-4 truncate ${player.placement === 1 ? 'text-yellow-400' :
                    player.placement === 2 ? 'text-gray-300' :
                        'text-orange-400'
                    }`}>
                    {player.username}
                </h3>

                <div className="flex flex-col space-y-2 text-center">
                    <div className="p-3">
                        <p className="text-xs font-mono text-green-400 mb-1">WAGERED</p>
                        <div className="flex items-center justify-center gap-1">
                            {formatWagered(player.wager).icon ? (
                                <>
                                    <p className="text-lg font-mono font-bold text-white">{formatWagered(player.wager).amount}</p>
                                    <Image
                                        src={formatWagered(player.wager).icon!}
                                        alt={`${currentBoard} icon`}
                                        width={16}
                                        height={16}
                                        className="w-3 h-3 object-contain"
                                    />
                                </>
                            ) : (
                                <p className="text-lg font-mono font-bold text-white">${formatWagered(player.wager).amount}</p>
                            )}
                        </div>
                    </div>

                    {player.placement && getPlacementPrize(player.placement) && (
                        <div>
                            <div className={`rounded-lg p-2 bg-gradient-to-r leaderboard-top-place-badge place-${player.placement} ${getPlacementColor(player.placement)}`}>
                                <div className="flex items-center justify-center gap-2">
                                    {getPlacementPrize(player.placement)!.icon ? (
                                        <>
                                            <span className="text-xl font-mono font-bold text-black">
                                                {getPlacementPrize(player.placement)!.amount}
                                            </span>
                                            <Image
                                                src={getPlacementPrize(player.placement)!.icon!}
                                                alt={`${currentBoard} icon`}
                                                width={24}
                                                height={24}
                                                className="w-4 h-4 object-contain"
                                            />
                                        </>
                                    ) : (
                                        <span className="text-xl font-mono font-bold text-black">
                                            ${getPlacementPrize(player.placement)!.amount}
                                        </span>
                                    )}
                                    {bonusPoints && (
                                        <>
                                            <div className="flex items-center gap-1 text-black/70 font-mono font-semibold text-sm">
                                                <span className="text-black">+{bonusPoints.toLocaleString()}</span>
                                                <CoinIcon size={12} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
TopPlayerCard.displayName = 'TopPlayerCard';

const PlayerRow = memo(({
    player,
    currentBoard,
    formatWagered,
    getPlacementPrize,
    getAvatarSrc,
    isCsgobigAvatar
}: {
    player: Participant;
    currentBoard: string;
    formatWagered: (amount: number) => { amount: string; icon: string | null };
    getPlacementPrize: (placement: number) => { amount: string; icon: string | null } | null;
    getAvatarSrc: (avatarPath: string | null | undefined) => string;
    isCsgobigAvatar: (avatarPath: string | null | undefined) => boolean;
}) => {
    const bonusPoints = player.placement ? bonusPointsPlacements[player.placement] : null;

    return (
        <div className="backdrop-blur border-b border-green-400/30 rounded-xl p-4 hover:border-green-400 transition-all group">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-400 via-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-mono font-bold text-black">{player.placement}</span>
                </div>

                <div className="w-12 h-12 rounded-full border-2 border-green-400 overflow-hidden flex-shrink-0">
                    <Image
                        src={getAvatarSrc(player.avatar)}
                        alt={player.username}
                        width={96}
                        height={96}
                        className="w-full h-full object-cover"
                        draggable="false"
                        referrerPolicy={isCsgobigAvatar(player.avatar) ? "no-referrer" : undefined}
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <p className="font-mono font-bold text-green-300 truncate">{player.username}</p>
                </div>

                <div className="hidden sm:block text-right">
                    <p className="text-xs font-mono text-green-400 mb-1">WAGERED</p>
                    <div className="flex items-center justify-end gap-1">
                        {formatWagered(player.wager).icon ? (
                            <>
                                <p className="font-mono font-bold text-white">{formatWagered(player.wager).amount}</p>
                                <Image
                                    src={formatWagered(player.wager).icon!}
                                    alt={`${currentBoard} icon`}
                                    width={16}
                                    height={16}
                                    className="w-3 h-3 object-contain"
                                />
                            </>
                        ) : (
                            <p className="font-mono font-bold text-white">${formatWagered(player.wager).amount}</p>
                        )}
                    </div>
                </div>

                {/* Prize + bonus points stacked on the right */}
                <div className="flex flex-col items-end gap-2">
                    {player.placement && getPlacementPrize(player.placement) && (
                        <div className="text-right backdrop-blur bg-green-400/10 rounded-lg px-4 py-2 border border-green-400/30">
                            <div className="flex items-center justify-end gap-1">
                                {getPlacementPrize(player.placement)!.icon ? (
                                    <>
                                        <p className="font-mono font-bold text-green-300">{getPlacementPrize(player.placement)!.amount}</p>
                                        <Image
                                            src={getPlacementPrize(player.placement)!.icon!}
                                            alt={`${currentBoard} icon`}
                                            width={16}
                                            height={16}
                                            className="w-3 h-3 object-contain"
                                        />
                                    </>
                                ) : (
                                    <p className="font-mono font-bold text-green-300">${getPlacementPrize(player.placement)!.amount}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Bonus points — below prize, original style */}
                    {bonusPoints && (
                        <div className="flex items-center justify-end gap-1 text-green-400 font-mono font-semibold text-xs px-1">
                            <span>+{bonusPoints.toLocaleString()}</span>
                            <CoinIcon size={11} />
                        </div>
                    )}
                </div>
            </div>

            <div className="sm:hidden mt-3 pt-3 border-t border-green-400/20">
                <div className="flex justify-between items-center">
                    <span className="text-xs font-mono text-green-400">WAGERED</span>
                    <div className="flex items-center gap-1">
                        {formatWagered(player.wager).icon ? (
                            <>
                                <span className="font-mono font-bold text-white">{formatWagered(player.wager).amount}</span>
                                <Image
                                    src={formatWagered(player.wager).icon!}
                                    alt={`${currentBoard} icon`}
                                    width={16}
                                    height={16}
                                    className="w-3 h-3 object-contain"
                                />
                            </>
                        ) : (
                            <span className="font-mono font-bold text-white">${formatWagered(player.wager).amount}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});
PlayerRow.displayName = 'PlayerRow';

export default function LeaderboardPage() {
    const [isMobile, setIsMobile] = useState(false);
    const params = useParams();
    const router = useRouter();
    const [currentBoard, setCurrentBoard] = useState('stake');
    const [isEnded, setIsEnded] = useState(false);

    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();

        let timeoutId: NodeJS.Timeout;
        const debouncedResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleResize, 150);
        };

        window.addEventListener('resize', debouncedResize);
        return () => {
            window.removeEventListener('resize', debouncedResize);
            clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        const board = params?.slug?.[0] ?? 'stake';
        const validBoards = leaderboardsConfig.map(lb => lb.id);
        if (!validBoards.includes(board)) {
            router.replace('/leaderboard/stake');
        } else {
            setCurrentBoard(board);
        }
    }, [params, router]);

    const fetchLeaderboard = useCallback(async (board: string, signal?: AbortSignal) => {
        const res = await axios.get(`/leaderboard/${board}`, { signal });
        return res.data;
    }, []);

    const {
        data: apiData,
        isLoading,
        isError
    } = useQuery<Leaderboard>({
        queryKey: ['leaderboard', currentBoard],
        queryFn: ({ signal }) => fetchLeaderboard(currentBoard, signal),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        retry: false,
    });

    const leaderboardData: Leaderboard | undefined = apiData;

    useEffect(() => {
        if (!leaderboardData?.startDate || !leaderboardData?.endDate || !leaderboardData.isRunning) {
            setIsEnded(true);
            return;
        }

        const endTime = new Date(leaderboardData.endDate).getTime();

        console.log('Leaderboard data:', {
            startDate: leaderboardData.startDate,
            endDate: leaderboardData.endDate,
            isRunning: leaderboardData.isRunning,
            endTime: new Date(leaderboardData.endDate).getTime(),
            now: Date.now(),
            distance: new Date(leaderboardData.endDate).getTime() - Date.now()
        });

        const calculateTimeLeft = () => {
            const now = Date.now();
            const distance = endTime - now;

            if (distance <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                setIsEnded(true);
                return false;
            }

            setIsEnded(false);
            setTimeLeft({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000)
            });
            return true;
        };

        calculateTimeLeft();
        const timer = setInterval(() => {
            if (!calculateTimeLeft()) {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [leaderboardData?.startDate, leaderboardData?.endDate, leaderboardData?.isRunning]);

    const handleSwitch = useCallback((board: string) => {
        if (board === currentBoard) return;
        router.push(`/leaderboard/${board}`);
    }, [currentBoard, router]);

    const getAvatarSrc = useCallback((avatarPath: string | null | undefined) => {
        if (avatarPath === "/assets/csgo/avatar-anonymous.png" ||
            avatarPath === "/assets/img/censored_avatar.png" ||
            avatarPath === null ||
            avatarPath === undefined) {
            return '/img/steam_profile.webp';
        }
        return avatarPath;
    }, []);

    const isCsgobigAvatar = useCallback((avatarPath: string | null | undefined) => {
        if (!avatarPath) return false;
        return avatarPath.includes('csgobig.com');
    }, []);

    const currentBoardConfig = useMemo(() =>
        leaderboardsConfig.find(lb => lb.id === currentBoard),
        [currentBoard]
    );

    const formatWagered = useCallback((amount: number) => {
        const iconSrc = currentBoardConfig?.icon ?? null;
        return {
            amount: amount.toLocaleString(),
            icon: iconSrc
        };
    }, [currentBoardConfig]);

    const getPlacementPrize = useCallback((placement: number) => {
        if (!leaderboardData?.prizePlacements) return null;

        const prizeInfo = leaderboardData.prizePlacements.find(
            p => p.place === placement
        );

        if (!prizeInfo) return null;

        const prizeAmount = `${prizeInfo.amount}`;
        const iconSrc = currentBoardConfig?.icon ?? null;

        return {
            amount: prizeAmount,
            icon: iconSrc
        };
    }, [leaderboardData?.prizePlacements, currentBoardConfig]);

    const showLeaderboardContent = !isLoading && !isError && (leaderboardData?.participants ?? []).length > 0;

    const top3Participants = useMemo(() =>
        leaderboardData?.participants.slice(0, 3) ?? [],
        [leaderboardData?.participants]
    );

    const remainingParticipants = useMemo(() =>
        leaderboardData?.participants.slice(3, 10) ?? [],
        [leaderboardData?.participants]
    );

    return (
        <div className="mt-15 min-h-screen text-green-400 overflow-hidden relative">
            <MatrixGrid />
            <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12 mt-8">
                    <h1 className="text-4xl md:text-6xl font-mono font-bold tracking-widest mb-4">
                        <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                            LEADERBOARDS
                        </span>
                    </h1>
                    <p className="text-green-300 font-mono text-sm md:text-base">
                        <span className="inline-block animate-pulse">{'['}</span> COMPETE • WAGER • WIN BIG PRIZES <span className="inline-block animate-pulse">{']'}</span>
                    </p>
                </div>

                {/* Leaderboard Selector */}
                <div className="flex flex-wrap gap-4 justify-center mb-12">
                    {leaderboardsConfig.map((lb) => (
                        <LeaderboardButton
                            key={lb.id}
                            lb={lb}
                            currentBoard={currentBoard}
                            isMobile={isMobile}
                            onSwitch={handleSwitch}
                        />
                    ))}
                </div>

                {currentBoard === 'sport' ? (
                    <SportLeaderboardView />
                ) : isLoading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-400"></div>
                        <p className="mt-4 font-bold font-mono text-green-400">Loading leaderboard...</p>
                    </div>
                ) : showLeaderboardContent ? (
                    <>
                        {/* Time Left Counter */}
                        <div className="max-w-2xl mx-auto mb-12 p-6">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <Clock className="w-6 h-6 text-green-400 animate-pulse" />
                                <h2 className="text-sm md:text-base font-mono font-bold text-green-400">
                                    {leaderboardData!.isRunning && !isEnded ? 'TIME LEFT' : 'ENDED'}
                                </h2>
                            </div>

                            <div className="grid grid-cols-4 gap-4">
                                <TimeDisplay value={leaderboardData!.isRunning && !isEnded ? timeLeft.days : 0} label="DAYS" />
                                <TimeDisplay value={leaderboardData!.isRunning && !isEnded ? timeLeft.hours : 0} label="HOURS" />
                                <TimeDisplay value={leaderboardData!.isRunning && !isEnded ? timeLeft.minutes : 0} label="MINS" />
                                <TimeDisplay value={leaderboardData!.isRunning && !isEnded ? timeLeft.seconds : 0} label="SECS" />
                            </div>
                        </div>

                        {/* Prize Pool */}
                        {leaderboardData!.totalPrize && (
                            <div className="text-center mb-8">
                                <div className="inline-block backdrop-blur border-b border-green-400/30 rounded-xl px-8 py-4">
                                    <p className="text-sm font-mono text-green-400 mb-1">TOTAL PRIZE POOL</p>
                                    <div className="flex items-center justify-center gap-2">
                                        {currentBoardConfig?.icon ? (
                                            <>
                                                <p className="text-2xl md:text-4xl font-mono font-bold text-green-300">{leaderboardData!.totalPrize}</p>
                                                <Image
                                                    src={currentBoardConfig.icon}
                                                    alt={`${currentBoard} icon`}
                                                    width={32}
                                                    height={32}
                                                    className="w-6 h-6 object-contain"
                                                />
                                            </>
                                        ) : (
                                            <p className="text-2xl md:text-4xl font-mono font-bold text-green-300">${leaderboardData!.totalPrize}</p>
                                        )}
                                    </div>
                                    {/* Bonus points row with info tooltip */}
                                    <div className="flex items-center justify-center gap-1.5 mt-2 text-green-400 font-mono font-semibold text-sm">
                                        <span>+ {totalBonusPoints.toLocaleString()}</span>
                                        <CoinIcon size={13} />
                                        <BonusPointsTooltip />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Top 3 Podium */}
                        {top3Participants.length >= 1 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto">
                                {top3Participants.map((player: Participant) => (
                                    <TopPlayerCard
                                        key={player.placement}
                                        player={player}
                                        currentBoard={currentBoard}
                                        formatWagered={formatWagered}
                                        getPlacementPrize={getPlacementPrize}
                                        getAvatarSrc={getAvatarSrc}
                                        isCsgobigAvatar={isCsgobigAvatar}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Remaining Rankings 4-10 */}
                        {remainingParticipants.length > 0 && (
                            <div className="max-w-4xl mx-auto">
                                <div className="space-y-3">
                                    {remainingParticipants.map((player: Participant) => (
                                        <PlayerRow
                                            key={player.placement}
                                            player={player}
                                            currentBoard={currentBoard}
                                            formatWagered={formatWagered}
                                            getPlacementPrize={getPlacementPrize}
                                            getAvatarSrc={getAvatarSrc}
                                            isCsgobigAvatar={isCsgobigAvatar}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Call to Action */}
                        <div className="text-center mt-12">
                            <p className="mb-8 text-green-300 font-mono text-sm md:text-base text-center">
                                <small>
                                    <span className="inline-block animate-pulse">
                                        NOTES: Any kind of wager abuse will <u>disqualify</u> you from the leaderboard.
                                    </span>
                                    <br />
                                    Amounts displayed in website currency. Leaderboard payouts are processed on the 5th of each month.
                                </small>
                            </p>
                            {currentBoardConfig && (
                                <Link
                                    href={currentBoardConfig.refferal_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 hover: transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden"
                                >
                                    <TrendingUp className="inline-block w-5 h-5 mr-2" />
                                    START WAGERING NOW
                                </Link>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="py-12">
                        <div className="flex flex-col items-center justify-center text-center text-green-400 font-bold font-mono">
                            <Frown className="w-16 h-16 mb-2" />
                            <p>There are no participants yet or the current leaderboard has ended/not started yet.</p>
                            {currentBoardConfig && (
                                <Link
                                    href={currentBoardConfig.refferal_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block mt-8 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 hover: transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden"
                                >
                                    <TrendingUp className="inline-block w-5 h-5 mr-2" />
                                    START WAGERING NOW
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}